import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ApiRequestStatus } from '../../../../model/api-request';
import { GlobalActions } from '../../../../model/global-actions';
import appRoutes from '../../../../routing/app-routes';
import {
  clearInsertPhoneNumberError,
  clearInsertPhoneNumberFields,
  resetInsertPhoneNumberRequestState,
  setPhoneNumber,
} from '../../../../store/features/user-access/user-registration/user-registration-slice';
import { insertPhoneNumber } from '../../../../store/features/user-access/user-registration/user-registration-thunk';
import { InsertPhoneNumberError } from '../../../../store/features/user-access/user-registration/user-registration-types';
import { messagesForInsertPhoneNumberError } from '../../../../store/features/user-access/user-registration/user-registration-utils';
import { useAppSelector } from '../../../redux';
import { PersonaTipoUtenteEnum } from '../../../../api';


export interface InsertPhoneNumberPageLogicCallbacks {
  buttonDisabled: boolean;
  phoneNumber: string;
  insertPhoneNumberRequestStatus: ApiRequestStatus;
  insertPhoneNumberError: InsertPhoneNumberError;
  phoneNumberAlreadyExisting: boolean;

  updatePhoneNumber: (value?: string) => void;
  handleSubmit: () => void;
  handleClose: () => void;
}

export const useInsertPhoneNumberPageLogic = (
  actions: GlobalActions
): InsertPhoneNumberPageLogicCallbacks => {
  const { navigate, showToast, closeToast } = actions;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const handleSubmit = useCallback(() => {
    dispatch(insertPhoneNumber());
  }, [dispatch]);

  const {
    phoneNumber,
    insertPhoneNumberRequestStatus,
    insertPhoneNumberError,
    phoneNumberAlreadyExisting,
    selectedOptionUserType
  } = useAppSelector((state) => state.features.userRegistration);

  const [buttonDisabled, setButtonDisabled] = useState(
    !phoneNumber || phoneNumber?.length === 0
  );

  const updatePhoneNumber = useCallback(
    (value: string) => {
      if (buttonDisabled) {
        setButtonDisabled(false);
      }
      dispatch(setPhoneNumber(value));
    },
    [dispatch, buttonDisabled]
  );

  useEffect(() => {
    if (insertPhoneNumberRequestStatus === 'succeeded') {
      if (phoneNumberAlreadyExisting) {
        if (selectedOptionUserType === PersonaTipoUtenteEnum.Pf){
          navigate(appRoutes.InsertSecondaryPhoneNumberUserRegistration);
        }else {
          navigate(appRoutes.SignatureAreaUserRegistration);
        }
      } else {
        navigate(appRoutes.PhoneNumberVerificationUserRegistration, {
          replace: false,
        });
      }
      dispatch(resetInsertPhoneNumberRequestState());
    }
  }, [
    dispatch,
    navigate,
    insertPhoneNumberRequestStatus,
    phoneNumberAlreadyExisting,
  ]);

  useEffect(() => {
    if (insertPhoneNumberError) {
      if (
        insertPhoneNumberError === 'GENERIC_ERROR' ||
        insertPhoneNumberError === 'INVALID_PHONE_NUMBER_FORMAT'
      ) {
        const errorMessages = messagesForInsertPhoneNumberError(
          insertPhoneNumberError
        );
        showToast({
          type: 'error',
          title: formatMessage(errorMessages.title),
          description:
            errorMessages.description &&
            formatMessage(errorMessages.description),
          close: closeToast,
        });
        dispatch(clearInsertPhoneNumberError());
      }
    }
  }, [dispatch, formatMessage, insertPhoneNumberError, closeToast, showToast]);

  const handleClose = useCallback(() => {
    dispatch(resetInsertPhoneNumberRequestState());
    dispatch(clearInsertPhoneNumberFields());
  }, [dispatch]);

  return {
    buttonDisabled,
    phoneNumber,
    insertPhoneNumberRequestStatus,
    insertPhoneNumberError,
    phoneNumberAlreadyExisting,
    handleSubmit,
    updatePhoneNumber,
    handleClose,
  };
};
